/**
 * Telegraph and zone decals in the art-test style: polygonOffset ground decals, a warm
 * orange fill (~0.3 alpha) that grows over the cast delay, an animated dashed edge ring.
 * Enemy areas are warm orange/red, friendly zones (Consecrated Ground) cool teal.
 * Danger reads through hue contrast against the dark, cool floor, not brightness.
 */
import * as THREE from 'three';
import type { AreaShape } from '@keystone/sim';
import type { RenderArea } from '../types';
import { groundIsFlat, groundY } from './ground';
import { NOISE_GLSL, U } from './style';
import { mechanicPalette } from './fx/mechanics/palette'; // +encounters
import { SAFE_WEDGE_LOOK } from './fx/enemyTable';

const SHAPE_ID: Record<AreaShape['type'], number> = { circle: 0, ring: 1, cone: 2, line: 3, rect: 4 };

/**
 * Local XY plane (yards) covering the shape; origin at the area origin, facing +x. On dungeons
 * with elevation the plane is subdivided (~1.5 yd cells) so it can bend over stairs and ledges.
 */
function decalGeometry(s: AreaShape, subdivide: boolean): THREE.PlaneGeometry {
  const pad = 0.1;
  const plane = (w: number, h: number): THREE.PlaneGeometry => {
    const seg = (x: number): number => (subdivide ? Math.max(1, Math.min(24, Math.ceil(x / 1.5))) : 1);
    return new THREE.PlaneGeometry(w, h, seg(w), seg(h));
  };
  switch (s.type) {
    case 'circle':
      return plane(2 * s.radius + pad, 2 * s.radius + pad);
    case 'ring':
      return plane(2 * s.outer + pad, 2 * s.outer + pad);
    case 'cone': {
      const half = Math.min(Math.PI, s.angle / 2);
      const minX = half > Math.PI / 2 ? -s.range * Math.sin(half - Math.PI / 2) : 0;
      const halfW = half > Math.PI / 2 ? s.range : s.range * Math.sin(half);
      return plane(s.range - minX + pad, 2 * halfW + pad).translate((s.range + minX) / 2, 0, 0);
    }
    case 'line':
      return plane(s.length + pad, s.width + pad).translate(s.length / 2, 0, 0);
    case 'rect':
      return plane(s.h + pad, s.w + pad);
  }
}

function shapeParams(s: AreaShape): [number, number] {
  switch (s.type) {
    case 'circle':
      return [s.radius, 0];
    case 'ring':
      return [s.outer, s.inner];
    case 'cone':
      return [s.range, s.angle / 2];
    case 'line':
      return [s.length, s.width];
    case 'rect':
      return [s.h, s.w];
  }
}

const VERT = `varying vec2 vLocal; void main(){ vLocal = position.xy; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`;

const FRAG = `
uniform float uTime; uniform float uFx; uniform int uShape; uniform int uKind; uniform float uA; uniform float uB;
uniform float uProgress; uniform vec3 uColor; uniform vec3 uCore; uniform float uSeed; uniform float uFill; uniform float uEdge; uniform float uSafe;
varying vec2 vLocal;
${NOISE_GLSL}
void main(){
  vec2 p = vLocal;
  float d = length(p);
  float norm; float edgeD; float along;
  if (uShape == 0) {            // circle
    if (d > uA) discard;
    norm = d / uA; edgeD = uA - d; along = atan(p.y, p.x) * d;
  } else if (uShape == 1) {     // ring (uA outer, uB inner)
    if (d > uA || d < uB) discard;
    norm = (d - uB) / max(uA - uB, 0.01); edgeD = min(d - uB, uA - d); along = atan(p.y, p.x) * d;
  } else if (uShape == 2) {     // cone (uA range, uB half angle)
    float ang = abs(atan(p.y, p.x));
    if (d > uA) discard;
    if (ang > uB) {
      // Wide (> 180°) cones leave a safe wedge behind the caster (Bloodroar): draw it unlit with a
      // pale dashed edge so "go here" reads against the warm danger around it.
      if (uB < 1.5708 || uSafe <= 0.0) discard;
      float sEdge = min(uA - d, (ang - uB) * d);
      float sRim = (1.0 - smoothstep(0.1, 0.22, sEdge)) * step(0.5, fract(d * 0.7 - (uTime + uSeed) * 0.8));
      float grow = smoothstep(0.0, 0.25, uProgress);
      vec3 sc = mix(vec3(0.0), vec3(0.62, 0.68, 0.72), sRim);
      float sa = max(uSafe * 0.55 * (1.0 - sRim), sRim * 0.5) * grow;
      gl_FragColor = vec4(sc * uFx, clamp(sa * uFx, 0.0, 1.0));
      return;
    }
    norm = d / uA; edgeD = min(uA - d, (uB - ang) * d); along = atan(p.y, p.x) * d;
  } else if (uShape == 3) {     // line: x in [0, uA], |y| <= uB/2
    if (p.x < 0.0 || p.x > uA || abs(p.y) > uB * 0.5) discard;
    norm = p.x / uA; edgeD = min(min(p.x, uA - p.x), uB * 0.5 - abs(p.y)); along = p.x + abs(p.y);
  } else {                      // rect: |x| <= uA/2 (along facing), |y| <= uB/2
    if (abs(p.x) > uA * 0.5 || abs(p.y) > uB * 0.5) discard;
    norm = (p.x + uA * 0.5) / uA; edgeD = min(uA * 0.5 - abs(p.x), uB * 0.5 - abs(p.y)); along = p.x + abs(p.y);
  }
  float t = uTime + uSeed;
  float pulse = 0.9 + 0.1 * sin(t * 6.0);
  float edge = 1.0 - smoothstep(0.12, 0.26, edgeD);
  float band = smoothstep(0.42, 0.48, edgeD) * (1.0 - smoothstep(0.62, 0.68, edgeD));
  float dash = step(0.5, fract(along * 0.55 - t * 1.6)) * band;
  vec3 col; float a;
  if (uKind == 0) {
    // Telegraph: faint base + growing fill + bright leading wave + edge ring.
    float filled = 1.0 - step(uProgress, norm);
    float wave = (1.0 - smoothstep(0.0, 0.035, abs(norm - uProgress))) * step(0.02, uProgress);
    a = 0.12 + filled * uFill + wave * 0.18 + dash * 0.22;
    a = max(a, edge * uEdge * pulse);
    col = mix(uColor, uCore, max(max(edge, dash), wave * 0.7));
  } else {
    // Zone: noisy pool, soft rolling ring, dashed rim, fades out with its remaining lifetime.
    float n = fbm(p * 0.45 + vec2(t * 0.18, -t * 0.12));
    float ring = 1.0 - smoothstep(0.0, 0.05, abs(norm - (1.0 - fract(t * 0.35))));
    a = uFill * (0.75 + n * 0.3 + ring * 0.3) + dash * uEdge * 0.3;
    a = max(a, edge * uEdge * pulse);
    a *= mix(0.45, 1.0, clamp(uProgress * 3.0, 0.0, 1.0));
    col = mix(uColor * (0.8 + 0.3 * n), uCore, max(edge, max(dash, ring * 0.5)));
  }
  gl_FragColor = vec4(min(col, vec3(1.0)) * uFx, clamp(a * uFx, 0.0, 1.0));
}`;

interface Palette {
  color: [number, number, number];
  core: [number, number, number];
  fill: number;
  edge: number;
}

const PALETTES: Record<'hostileTelegraph' | 'hostileZone' | 'friendly', Palette> = {
  hostileTelegraph: { color: [0.95, 0.32, 0.07], core: [1.0, 0.62, 0.28], fill: 0.3, edge: 0.72 },
  hostileZone: { color: [0.86, 0.2, 0.08], core: [1.0, 0.5, 0.22], fill: 0.28, edge: 0.65 },
  // Friendly zones are information, not danger: cool, thin and low-alpha.
  friendly: { color: [0.1, 0.55, 0.56], core: [0.38, 0.85, 0.8], fill: 0.07, edge: 0.34 },
};

let seq = 0;

export class AreaDecal {
  readonly group = new THREE.Group();
  private readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;

  constructor(a: RenderArea) {
    const shape = a.area.shape;
    const pal = mechanicPalette(a) ?? (a.hostile ? (a.kind === 'telegraph' ? PALETTES.hostileTelegraph : PALETTES.hostileZone) : PALETTES.friendly);
    const [pa, pb] = shapeParams(shape);
    this.mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime: U.uTime,
        uFx: U.uFx,
        uShape: { value: SHAPE_ID[shape.type] },
        uKind: { value: a.kind === 'telegraph' ? 0 : 1 },
        uA: { value: pa },
        uB: { value: pb },
        uProgress: { value: a.progress },
        uColor: { value: new THREE.Vector3(...pal.color) },
        uCore: { value: new THREE.Vector3(...pal.core) },
        uFill: { value: pal.fill },
        uEdge: { value: pal.edge },
        uSeed: { value: (seq * 1.37) % 10 },
        uSafe: { value: a.hostile && a.kind === 'telegraph' ? SAFE_WEDGE_LOOK.dim : 0 },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      polygonOffset: true,
      polygonOffsetFactor: -2,
      polygonOffsetUnits: -2,
    });
    this.mesh = new THREE.Mesh(decalGeometry(shape, !groundIsFlat()), this.mat);
    this.mesh.rotation.x = -Math.PI / 2;
    // Friendly zones under enemy telegraphs; later telegraphs on top.
    this.mesh.renderOrder = (a.hostile ? 3 : 2) + (seq++ % 8) * 0.01;
    this.group.add(this.mesh);
    this.group.position.y = 0.03;
  }

  update(a: RenderArea): void {
    const o = a.area.origin;
    const moved = Math.abs(o.x - this.lastX) > 0.05 || Math.abs(o.y - this.lastY) > 0.05 || Math.abs(a.area.facing - this.lastF) > 0.01;
    if (moved) {
      this.lastX = o.x;
      this.lastY = o.y;
      this.lastF = a.area.facing;
      this.baseY = groundY(o.x, o.y);
      this.conform(o.x, o.y, a.area.facing);
    }
    this.group.position.set(o.x, this.baseY + 0.03, -o.y);
    this.group.rotation.y = a.area.facing;
    (this.mat.uniforms['uProgress'] as { value: number }).value = a.progress;
  }

  private lastX = NaN;
  private lastY = NaN;
  private lastF = NaN;
  private baseY = 0;
  private bent = false;

  /**
   * Bend the decal over the floor (stairs, platform edges): each vertex takes the floor height
   * under it, relative to the origin's. Skipped on flat ground (9-sample check), so telegraphs
   * on a level floor cost nothing extra; only re-run when the area moves or turns.
   */
  private conform(ox: number, oy: number, facing: number): void {
    if (groundIsFlat()) return;
    const geo = this.mesh.geometry;
    const pos = geo.attributes['position'] as THREE.BufferAttribute;
    if (!geo.boundingBox) geo.computeBoundingBox();
    const bb = geo.boundingBox!;
    const c = Math.cos(facing);
    const s = Math.sin(facing);
    const g0 = this.baseY;
    const at = (lx: number, ly: number): number => groundY(ox + lx * c - ly * s, oy + lx * s + ly * c) - g0;
    let flat = true;
    for (const fx of [0, 0.5, 1])
      for (const fy of [0, 0.5, 1]) {
        if (Math.abs(at(bb.min.x + (bb.max.x - bb.min.x) * fx, bb.min.y + (bb.max.y - bb.min.y) * fy)) > 0.01) flat = false;
      }
    if (flat) {
      if (this.bent) {
        for (let i = 0; i < pos.count; i++) pos.setZ(i, 0);
        pos.needsUpdate = true;
        this.bent = false;
      }
      return;
    }
    for (let i = 0; i < pos.count; i++) pos.setZ(i, at(pos.getX(i), pos.getY(i)));
    pos.needsUpdate = true;
    this.bent = true;
  }

  dispose(): void {
    this.group.removeFromParent();
    this.mesh.geometry.dispose();
    this.mat.dispose();
  }
}
